import React, { useState } from 'react';
import {
  XMarkIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import { MemoryEntryContent, MediaContent } from '~/services/memory';

interface MemoryContentPreviewProps {
  content: MemoryEntryContent;
  maxLength?: number;
}

interface MemoryMediaPreviewProps {
  media: MediaContent;
  size?: 'small' | 'medium' | 'large';
}

export const MemoryContentPreview = ({
  content,
  maxLength = 150,
}: MemoryContentPreviewProps) => {
  const { text, media } = content;

  // Truncate text if it's too long
  const truncatedText =
    text && text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;

  return (
    <div className="space-y-2">
      {/* Text content */}
      {text && <div className="text-sm text-gray-700">{truncatedText}</div>}

      {/* Media content */}
      {media && media.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {media.map((mediaItem, index) => (
            <MemoryMediaPreview
              key={`${mediaItem.url}-${index}`}
              media={mediaItem}
              size="small"
            />
          ))}
        </div>
      )}

      {/* Show placeholder if no content */}
      {!text && (!media || media.length === 0) && (
        <div className="text-sm italic text-gray-400">No content available</div>
      )}
    </div>
  );
};

export const MemoryMediaPreview = ({
  media,
  size = 'small',
}: MemoryMediaPreviewProps) => {
  const [showPreview, setShowPreview] = useState(false);

  const sizeClass = {
    small: 'h-8 w-8',
    medium: 'h-16 w-16',
    large: 'h-24 w-24',
  }[size];

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPreview(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPreview(false);
  };

  return (
    <>
      <div
        className={`inline-flex cursor-pointer items-center justify-center overflow-hidden rounded-md border bg-gray-50 ${sizeClass}`}
        onClick={handleClick}
        title={media.metadata.name}
      >
        {media.metadata.mimetype.startsWith('image/') ? (
          <img
            src={`${import.meta.env.VITE_API_URL}/media/${media.url}`}
            alt={media.metadata.name}
            className="h-full w-full object-cover"
          />
        ) : media.metadata.mimetype.startsWith('video/') ? (
          <div className="relative h-full w-full">
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <video
              src={`${import.meta.env.VITE_API_URL}/media/${media.url}`}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <DocumentMagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        )}
      </div>

      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={handleClose}
        >
          <div
            className="max-w-3xl relative max-h-[80vh] rounded-lg bg-white p-2 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute right-2 top-2 z-10 rounded-full bg-white bg-opacity-75 p-1 text-gray-700 hover:text-gray-900"
              onClick={handleClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <div className="max-h-[calc(80vh-6rem)] overflow-auto">
              {media.metadata.mimetype.startsWith('image/') ? (
                <img
                  src={`${import.meta.env.VITE_API_URL}/media/${media.url}`}
                  alt={media.metadata.name}
                  className="mx-auto max-h-[calc(80vh-8rem)] w-auto object-contain"
                />
              ) : media.metadata.mimetype.startsWith('video/') ? (
                <video
                  src={`${import.meta.env.VITE_API_URL}/media/${media.url}`}
                  className="mx-auto max-h-[calc(80vh-8rem)] w-auto"
                  controls
                  autoPlay
                />
              ) : (
                <div className="flex h-40 flex-col items-center justify-center">
                  <DocumentMagnifyingGlassIcon className="h-16 w-16 text-gray-400" />
                  <p className="mt-2 text-gray-500">Preview not available</p>
                </div>
              )}
            </div>

            <div className="mt-4 border-t pt-2">
              <h3 className="font-medium text-gray-900">
                {media.metadata.name}
              </h3>
              {media.metadata.description && (
                <p className="text-sm text-gray-600">
                  {media.metadata.description}
                </p>
              )}
              <p className="text-xs text-gray-500">
                {media.metadata.mimetype} •{' '}
                {(media.metadata.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MemoryContentPreview;
